import * as api from '@api';
import HelpButton from '@components/help-button';
import LoginMap from '@components/map';
import { yupResolver } from '@hookform/resolvers/yup';
import config from '@lib/config';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  NewBox as Box,
  Button,
  Card,
  CardContent,
  Flex,
  Heading,
  Input,
  Text,
  useToast,
} from '@retracedgmbh/ui-components-lib';
import { useAuth } from '@state/auth';
import type React from 'react';
import { useEffect, useState } from 'react';
import { type Resolver, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .matches(
      /^[^@ \t\r\n]+@[^@ \t\r\n]+\.[^@ \t\r\n]+$/,
      'Must be a valid email.',
    )
    .required('The email is required.'),
  code: Yup.string().length(6, 'The code has exactly 6 digits.').optional(),
});

type LoginFormValues = Yup.InferType<typeof loginSchema>;

const prepareSubmitParams = (value: LoginFormValues) => ({
  email: (value.email ?? '').trim(),
});
const validateEmail = (email: string) => {
  const re =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
  return re.test(String(email).toLowerCase());
};

const LoginForm = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSystemFailure, setIsSystemFailure] = useState(false);
  const [isWithCode, setIsWithCode] = useState(false);
  const [isResendCodeButtonDisabled, setIsResendCodeButtonDisabled] =
    useState(false);
  const [isSsoLoading, setIsSsoLoading] = useState(false);
  const [flowId, setFlowId] = useState<string | null>(null);

  const login = useAuth(state => state.login);

  const {
    register,
    formState,
    setValue,
    unregister,
    setFocus,
    handleSubmit,
    setError,
    getValues,
    clearErrors,
    reset,
  } = useForm<LoginFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(loginSchema as any) as Resolver<LoginFormValues>,
  });
  const { isSubmitting, errors } = formState;

  const onSubmit = async (value: LoginFormValues) => {
    const param = prepareSubmitParams(value);
    if (isWithCode) {
      return api.backend
        .login({ ...param, code: value.code, ...(flowId && { flowId }) })
        .then(response => {
          if (response.status >= 400) {
            setError('code', {
              message: response.error?.message,
            });
          } else {
            login(response.data);
            navigate('/', { replace: true });
          }
        });
    }

    return api.backend
      .requestCode(param)
      .then(response => {
        if (response.status >= 400)
          setError('email', {
            message: response.error?.message,
          });
        else {
          setIsWithCode(true);
          if (response.data?.flowId) {
            setFlowId(response.data.flowId);
          }
          setFocus('code');
        }
      })
      .catch(() => setIsSystemFailure(true));
  };

  const handleResendCode = async () => {
    const values = getValues();
    const param = prepareSubmitParams(values);

    clearErrors(['email', 'code']);
    reset({
      email: values?.email,
      code: '',
    });

    setIsResendCodeButtonDisabled(true);

    return api.backend
      .requestCode(param)
      .then(response => {
        if (response.status >= 400) {
          setError('email', {
            message: response.error?.message,
          });
          setIsResendCodeButtonDisabled(response.status === 403);
        } else {
          setIsWithCode(true);
          setFocus('code');
          if (response.data?.flowId) {
            setFlowId(response.data.flowId);
          }
          setTimeout(() => setIsResendCodeButtonDisabled(false), 3000);
          toast({
            description:
              'We just sent you the new code. Please refresh your inbox.',
            variant: 'success',
          });
        }
      })
      .catch(() => {
        setIsSystemFailure(true);
        setIsResendCodeButtonDisabled(false);
      });
  };

  const onSubmitSSO = async () => {
    const values = getValues();
    const isEmail = validateEmail(values.email);
    if (!isEmail) {
      setError('email', {
        message: 'Single sign on can only work with email',
      });
      return;
    }

    setIsSsoLoading(true);
    try {
      const response = await api.backend.authenticationByDomain(values.email);
      if (response.status >= 400) {
        setError('email', {
          message:
            'Your company has not registered yet for Single sign on, please try to login with Request Login Code',
        });
        setIsSsoLoading(false);
      } else {
        window.location.href = response.data.authorizationUrl;
      }
    } catch {
      setIsSystemFailure(true);
      setIsSsoLoading(false);
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = event.clipboardData.getData('text/plain');
    const formattedText = pastedText.replace(/\s/g, '');
    setValue('code', formattedText);
    event.preventDefault();
  };

  return (
    <Flex direction='column'>
      <form onSubmit={handleSubmit(onSubmit)}>
        {isSystemFailure && (
          <Alert variant='destructive' className='tw:mb-5'>
            <AlertTitle>
              <i className='fa-regular fa-circle-exclamation tw:mr-2' />
              System unreachable
            </AlertTitle>
            <AlertDescription className='tw:max-w-[100%]'>
              <Flex gap='md' direction='column'>
                <Text as='span'>
                  The system is not reachable from your location at this moment.
                  Please keep an eye out on the status page for details. If the
                  error persists, please contact us.
                </Text>
                <Link
                  to='https://status.retraced.com'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  <Text as='span'>Check the status page</Text>
                </Link>
              </Flex>
            </AlertDescription>
          </Alert>
        )}

        {isWithCode ? (
          <>
            <Button
              icon='fa-light fa-chevron-left'
              title='Back'
              variant='ghost'
              color='primary'
              className='tw:pl-0 tw:pt-0'
              onClick={() => {
                setIsWithCode(false);
                unregister('code');
              }}
            />
            <Text as='p' data-testid='otp-sent-message'>
              We just sent you a temporary login code. Please check your inbox.
            </Text>
          </>
        ) : (
          <>
            <Heading as='h3' color='primary' weight='bold'>
              Welcome back
            </Heading>
            <Text as='p' marginTop='xl'>
              The login just requires your email address you used for
              registration. You will receive a one-time code which you have to
              enter afterwards
            </Text>
          </>
        )}

        <Box className='field' marginTop='xl'>
          <Text as='p' className='tw:text-left tw:mb-2'>
            Email
          </Text>
          <Input
            {...register('email')}
            type='text'
            placeholder='example@email.com'
            name='email'
            error={Boolean(errors.email)}
            defaultValue={getValues('email')}
            readOnly={isWithCode}
            id='email'
          />
          {errors.email && (
            <Text as='p' className='help' align='left' color='negative'>
              {errors.email.message}
            </Text>
          )}

          {isWithCode && (
            <>
              <Text as='p' className='tw:text-left tw:mb-2 tw:mt-4'>
                Code
              </Text>
              <Box className='control is-expanded'>
                <Input
                  {...register('code')}
                  data-dd-privacy='mask-user-input'
                  error={Boolean(errors.code)}
                  type='text'
                  placeholder='xxxxxx'
                  name='code'
                  id='code'
                  onPaste={handlePaste}
                  maxLength={6}
                  data-testid='otp-input'
                />
                {errors.code && (
                  <Text as='p' className='help' align='left' color='negative'>
                    {errors.code.message}
                  </Text>
                )}
              </Box>

              <Box className='login-footer'>
                <Button
                  type='button'
                  variant='ghost'
                  color='primary'
                  size='sm'
                  title='Request new code'
                  disabled={isSubmitting || isResendCodeButtonDisabled}
                  onClick={handleResendCode}
                />

                <HelpButton
                  articleId='8508502-troubleshooting-login-issues'
                  size='sm'
                  style={{
                    paddingRight: 0,
                    backgroundColor: 'transparent',
                    textDecoration: 'underline',
                    lineHeight: '10px',
                    textUnderlineOffset: '7px',
                  }}
                  text='Where is my login code?'
                />
              </Box>
            </>
          )}
        </Box>

        <Box className='is-centered' marginTop='xl'>
          <Button
            type='submit'
            variant='primary'
            isFullWidth
            loading={Boolean(isSubmitting)}
            disabled={isSubmitting}
            title={isWithCode ? 'Login' : 'Request Login Code'}
            loadingText={isWithCode ? 'Logging you in' : 'Sending you the code'}
            id='submit'
          />
        </Box>

        {!isWithCode && (
          <>
            <Box className='separator tw:mt-5'>
              <Text as='span'>Or sign in with</Text>
            </Box>

            <Box marginTop='md' className='control is-centered'>
              <Button
                type='button'
                isFullWidth
                variant='outline'
                disabled={isSubmitting || isSsoLoading}
                loading={isSsoLoading}
                loadingText='Redirecting...'
                onClick={onSubmitSSO}
              >
                Single Sign On
              </Button>
            </Box>
          </>
        )}

        <Flex alignItems='center' justify='center' gap='xl' marginTop='2xl'>
          <Link
            to='https://www.retraced.com/pages/privacy-policy'
            target='_blank'
            className='tw:text-primary tw:text-xs'
            rel='noreferrer'
          >
            Privacy Policy
          </Link>
          <Box className='tw:h-[15px] tw:w-[1px] tw:bg-grey' />
          <Link
            to='https://www.retraced.com/pages/imprint'
            target='_blank'
            className='tw:text-primary tw:text-xs'
            rel='noreferrer'
          >
            Imprint
          </Link>
        </Flex>
      </form>
    </Flex>
  );
};

const isUserLoggedIn = (
  accessToken: string | null,
  refreshToken: string | null,
  isSessionExpired: boolean | null,
) => {
  if (config.isCookieEnabled) {
    return !(isSessionExpired === null || isSessionExpired);
  }

  return Boolean(accessToken) || Boolean(refreshToken);
};

const Login = () => {
  const navigate = useNavigate();
  const { accessToken, refreshToken, isSessionExpired } = useAuth(state => ({
    accessToken: state.accessToken,
    refreshToken: state.refreshToken,
    isSessionExpired: state.isSessionExpired,
  }));
  const isPossiblyAuthenticated = isUserLoggedIn(
    accessToken,
    refreshToken,
    isSessionExpired,
  );

  useEffect(() => {
    if (isPossiblyAuthenticated) navigate('/');
  }, [isPossiblyAuthenticated, navigate]);

  if (isPossiblyAuthenticated) return null;

  return (
    <section className='hero is-fullheight login-page'>
      <Box className='retraced-light-logo'>
        <img
          src={`${import.meta.env.VITE_CDN_URI}/static/dashboard-service/secondary-logo-cloud-v2.svg`}
          alt='retraced with text'
          width='150'
          height='60'
          loading='lazy'
        />
        <Heading as='h2' weight='normal'>
          Next generation supply chain transparency. At your finger tips.
        </Heading>
      </Box>
      <Box className='hero-body' padding='none'>
        <Box
          className='columns is-centered tw:w-full'
          padding='sm'
          margin='none'
        >
          <Box className='column is-half-tablet is-three-fifths-hd is-centered '>
            <img
              src={`${import.meta.env.VITE_CDN_URI}/static/dashboard-service/secondary-logo-black-v2.svg`}
              className='tw:mb-5 tw:mx-auto is-hidden-tablet'
              alt='retraced with text'
              width='150'
              height='60'
              loading='lazy'
            />
          </Box>
          <Card className='login-container'>
            <CardContent>
              <LoginForm />
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Box id='background_image'>
        <LoginMap />
        <img
          alt='Traceability Map'
          id='image0_202_138'
          width='100%'
          height='1734'
          src={`${import.meta.env.VITE_CDN_URI}/static/dashboard-service/map.svg`}
          loading='lazy'
        />
      </Box>
    </section>
  );
};

export default Login;
